import "@tento-ui/components/round";

import { LonaWebComponent, html } from "@tento-ui/component";
import { Column, Hr } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Anchor, AnchorInstance } from "@tento-ui/components/anchor";
import { Button } from "@tento-ui/components/button/button";
import { Divider } from "@tento-ui/components/divider";
import { Position } from "@tento-ui/anchor-positioning-utils";
import { Round } from "@tento-ui/components/round";
import { TimeScaleSelector } from "@tento-ui/components/time-scale-selector/time-scale-selector";
import { TimeScaleSelectorIntervalRow } from "@tento-ui/components/time-scale-selector/time-scale-selector-interval-row";
import { Tooltip } from "@tento-ui/components/tooltip/tooltip";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { AngleRightIcon, LonaIcons, SearchIcon } from "@tento-ui/ui/icons";
import { type Option, SheetViewConfig, type Timescale } from "@tento-lona/sheets";
import { LiveData } from "@tento-core/livedata";
import { SheetElement } from "@tento-lona/sheets-ui/sheet/sheet-element.ts";
import { Disclaimer } from "@tento-ui/components/disclaimer";
import { Typography } from "@tento-ui/ui/typography";
/**
 * The `lona-sheet-action-bar` element: a sheet's floating bar of view
 * controls.
 */
@component({
  name: "lona-sheet-action-bar",
})
export class SheetActionBar extends LonaWebComponent {
  showYearTimescale = false;
  #$viewConfigPopover: Option<AnchorInstance> = null;
  // Floating bar fill: keep the base bg (--bg, white) in LIGHT mode, but in dark
  // mode use the lighter primary-button shade (--bg-btn-pri = #334155) so the bar
  // reads as raised. Nested light-dark resolves --bg-btn-pri to its dark value.
  #$bg = this.hydrate<Round>("bg").bind({
    ...Round.Props.MODAL,
    bg: "light-dark(var(--bg), var(--bg-btn-pri))",
  });
  // Static text, bound once; the tooltip attaches to its parent button.
  #$searchTooltip = this.hydrate<Tooltip>("search-tooltip").bind({
    text: "Search cmd-k",
    placement: "top",
    delayMs: null,
  });

  initCallbacks(callbacks: {
    previousClicked: () => void;
    nextClicked: () => void;
    todayClicked: () => void;
    searchClicked: () => void;
    cellWidthToggleClicked: () => void;
  }) {
    // Cleanup previous GESTURE_MANAGER handlers before rebinding
    GESTURE_MANAGER.unregister(this.$("previous-week-button"));
    GESTURE_MANAGER.unregister(this.$("next-week-button"));
    GESTURE_MANAGER.unregister(this.$("today-btn"));
    GESTURE_MANAGER.unregister(this.$("search-btn"));
    GESTURE_MANAGER.unregister(this.$("cell-width-toggle"));

    GESTURE_MANAGER.addPointerEvent(this.$("previous-week-button"), {
      onClick: callbacks.previousClicked,
    });
    GESTURE_MANAGER.addPointerEvent(this.$("next-week-button"), {
      onClick: callbacks.nextClicked,
    });
    GESTURE_MANAGER.addPointerEvent(this.$("today-btn"), {
      onClick: callbacks.todayClicked,
    });
    GESTURE_MANAGER.addPointerEvent(this.$("search-btn"), {
      onClick: callbacks.searchClicked,
    });
    GESTURE_MANAGER.addPointerEvent(this.$("cell-width-toggle"), {
      onClick: callbacks.cellWidthToggleClicked,
    });
  }

  toggleViewConfigPopover(
    viewConfig: SheetViewConfig,
    resolved: LiveData.Readable<Option<SheetElement.Width>>,
    onSelect: (viewConfig: SheetViewConfig) => void,
    defaultValues: Option<Map<Timescale, number>>,
  ) {
    if (this.#$viewConfigPopover != null) {
      this.#$viewConfigPopover.dismiss();
    } else {
      this.#showViewConfigPopover(viewConfig, resolved, onSelect, defaultValues);
    }
  }

  #showViewConfigPopover(
    viewConfig: SheetViewConfig,
    resolved: LiveData.Readable<Option<SheetElement.Width>>,
    onSelect: (viewConfig: SheetViewConfig) => void,
    defaultValues: Option<Map<Timescale, number>>,
  ) {
    const $label = Typography.p("Column Width");
    $label.classList.add("label");
    $label.textContent = "Column Width";
    $label.assignStyles({
      "--margin-top": "8px",
      "--margin-bottom": "8px",
      marginInline: "12px",
      color: "var(--text-sec)",
    });

    const $content = Column.make();
    $content.style.padding = "8px";
    $content.style.maxHeight = "400px";
    $content.style.overflowY = "auto";
    $content.appendChild($label);
    $content.appendChild(Hr.make());

    const types: Timescale[] = this.showYearTimescale
      ? ["year", "month", "week", "day"]
      : ["month", "week", "day"];

    const $selector = TimeScaleSelector.makeWith({
      defaultValues,
      types,
      value: viewConfig.count,
      type: viewConfig.timescale,
      usePersonalTimezones:
        viewConfig.timescale === "day"
          ? viewConfig.tzType === "time-traveler"
          : false,
      onChange: (value, type, usePersonalTimezone) => {
        switch (type) {
          case "week":
          case "year":
          case "month": {
            onSelect({
              count: value,
              timescale: type,
            });
            break;
          }
          case "day": {
            onSelect({
              count: value,
              timescale: "day",
              tzType: usePersonalTimezone ? "time-traveler" : "default",
            });
          }
        }
      },
    });

    const $disclaimer = Disclaimer.make();
    $disclaimer.style.color = "var(--text-sec)";
    resolved.addListener(
      $disclaimer,
      (r) => {
        if (!r) {
          $disclaimer.style.display = "none";
          return;
        }
        $disclaimer.style.display = r.match({
          count: (e) => {
            $disclaimer.bind(`Unable to fit more than ${e} columns`);
            return e === $selector.currentValue ? "none" : "block";
          },
          px: () => "none",
        });
      },
      LiveData.TriggerImmediately,
    );

    $content.appendChild($disclaimer);
    $content.appendChild($selector);

    const $container = Round.makeWithContent($content, {
      bg: "var(--bg-elevated)",
      radius: 8,
      shadow: "var(--box-shadow)",
    });
    $container.assignStyles({
      height: "fit-content",
    });

    $container.assignStyles({ width: `${this.offsetWidth}px` });
    const instance = Anchor.reveal({
      $anchor: this.$("root"),
      $content: $container,
      position: Position.TOP,
      offset: {
        outer: {
          vertical: 4,
          horizontal: 0,
        },
      },
      dismissOnClickOutside: false, // Custom click-outside handler below
      onDismiss: () => {
        if (instance !== this.#$viewConfigPopover) return;
        this.#$viewConfigPopover = null;
        resolved.removeListener($disclaimer);
      },
    });

    GESTURE_MANAGER.setOnClickOutside($container, ($e) => {
      if ($e) {
        const $a = DomUtils.getAncestors($e);
        for (const $p of [$e, ...$a]) {
          if ($p instanceof TimeScaleSelectorIntervalRow.Modal) return;
          // TODO: pretty hacky but eh
          if ($e.id === "cell-width-toggle") return;
        }
      }
      instance.dismiss();
      GESTURE_MANAGER.clearOnClickOutside($container);
    });
    this.#$viewConfigPopover = instance;
  }

  static $styles: Style.Compat[] = [
    Button.$style,
    Divider.$vr,
    Divider.$hr,
    css`
      :host {
        margin-block: 5px;
      }

      #cell-width-toggle-anchor,
      #today-btn {
        width: 90px;
      }

      #root {
        padding: 4px 8px;
        align-items: center;
        height: 38px;
      }
    `,
  ];

  static $icons: Option<string[]> = [
    LonaIcons.symbol(AngleRightIcon),
    LonaIcons.symbol(SearchIcon),
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-row id=root>
      <std-round id=bg floating></std-round>
      <std-icon-button
        id="previous-week-button"
        aria-label="Previous week"
        squarish
        style="margin-right:2px;"
      >
        <svg width="16" height="16" style="transform:rotate(180deg)">
          <use href="#angle-right" />
        </svg>
      </std-icon-button>
      <button id="today-btn" class="flat-button">
        <p class="small" style="--margin-top:2px;--margin-bottom:2px;">Today</p>
      </button>
      <std-vr style="align-self:stretch;margin-block:2px;"></std-vr>
      <div id="cell-width-toggle-anchor" style="position:relative;">
        <button
          id="cell-width-toggle"
          class="flat-button"
          style="min-width:80px"
        >
          <slot name="cell-width-toggle-text"></slot>
        </button>
      </div>
      <std-icon-button
        id="search-btn"
        aria-label="Search"
        squarish
        style="position:relative;display:none"
      >
        <svg id="search-icon">
          <use href="#search" />
        </svg>
        <std-tooltip id="search-tooltip"></std-tooltip>
      </std-icon-button>
      <std-icon-button
        id="next-week-button"
        aria-label="Next week"
        squarish
        style="margin-left:2px;"
      >
        <svg width="16" height="16" style="transform:rotate(0deg)">
          <use href="#angle-right" />
        </svg>
      </std-icon-button>
    </std-row>
  `;
}
